import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { sprawdzNrb, cyfryNrb } from '../lib/nrb';
import { firestore } from '../lib/firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Modal from './Modal';
import Button from './Button';
import FormInput from './FormInput';
import FormLabel from './FormLabel';
import BlockTitle from './BlockTitle';
import { useToast } from '../context/ToastContext';

const functions = getFunctions(undefined, 'europe-central2');
const parseContractFunction = httpsCallable(functions, 'parseContract');
const uploadToDriveFunction = httpsCallable(functions, 'uploadToDrive');

// Callable przyjmuje 10 MB na zadanie, a PDF leci w base64, czyli o jedna
// trzecia grubszy. Zadanie z wiekszym plikiem odbija sie ZANIM funkcja
// wystartuje - w logach nie ma po nim sladu, a doradca widzial tylko "Blad
// parsowania dokumentu. Sprobuj ponownie", wiec probowal w kolko.
// Sprawdzamy wiec rozmiar tutaj i mowimy wprost, co jest nie tak.
const LIMIT_CALLABLE_MB = 10;
const NARZUT_BASE64 = 4 / 3;
const MAX_PDF_BAJTOW = Math.floor((LIMIT_CALLABLE_MB * 1024 * 1024) / NARZUT_BASE64);
const mb = (bajty) => (bajty / (1024 * 1024)).toFixed(1).replace('.', ',');

// Loose name comparator: lowercases, strips diacritics, collapses non-alphanum.
const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');

const looseMatch = (a, b) => {
  const na = normalize(a);
  const nb = normalize(b);
  if (!na || !nb) return true; // nothing to compare against
  return na.includes(nb) || nb.includes(na);
};

const UploadContractModal = ({ isOpen, onClose, investmentId, onSave }) => {
  const { showSuccess, showError } = useToast();
  const [step, setStep] = useState('upload'); // upload | parsing | review | saving
  const [fileName, setFileName] = useState('');
  const [parsedData, setParsedData] = useState(null);

  // Investment context (for validation against PDF)
  const [investmentDoc, setInvestmentDoc] = useState(null);

  // Store PDF base64 for Drive upload
  const [pdfBase64, setPdfBase64] = useState(null);

  // Editable fields
  const [contractNumber, setContractNumber] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [flatCode, setFlatCode] = useState('');
  const [flatSize, setFlatSize] = useState('');
  const [priceGross, setPriceGross] = useState('');
  const [buyers, setBuyers] = useState([{ name: '', surname: '', address: '', pesel: '', nip: '', buyerType: 'osoba_fizyczna', companyName: '', krs: '', share: '' }]);
  const [ownershipType, setOwnershipType] = useState('sole'); // sole | joint_marital | fractional
  const [maritalCommunity, setMaritalCommunity] = useState(false);
  const [paymentSchedule, setPaymentSchedule] = useState([]);
  const [parkingSpace, setParkingSpace] = useState('');
  const [storageUnit, setStorageUnit] = useState('');
  // Rachunek nabywcy z aktu. Bez niego wplaty z banku nie trafiaja do lokalu.
  const [accountNumber, setAccountNumber] = useState('');
  const [accountNote, setAccountNote] = useState('');

  // Load investment doc once when modal opens for given investmentId
  useEffect(() => {
    if (!isOpen || !investmentId) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(firestore, `investments/${investmentId}`));
        if (!cancelled && snap.exists()) {
          setInvestmentDoc({ id: snap.id, ...snap.data() });
        }
      } catch (err) {
        console.error('Failed to load investment for validation:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, investmentId]);

  const resetForm = () => {
    setStep('upload');
    setFileName('');
    setParsedData(null);
    setPdfBase64(null);
    setContractNumber('');
    setContractDate('');
    setFlatCode('');
    setFlatSize('');
    setPriceGross('');
    setBuyers([{ name: '', surname: '', address: '', pesel: '', nip: '', buyerType: 'osoba_fizyczna', companyName: '', krs: '', share: '' }]);
    setOwnershipType('sole');
    setMaritalCommunity(false);
    setPaymentSchedule([]);
    setParkingSpace('');
    setStorageUnit('');
    setAccountNumber('');
    setAccountNote('');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || file.type !== 'application/pdf') {
      showError('Wybierz plik PDF');
      return;
    }

    if (file.size > MAX_PDF_BAJTOW) {
      showError(
        `Plik ma ${mb(file.size)} MB, a limit to ${mb(MAX_PDF_BAJTOW)} MB. `
        + 'Skompresuj PDF albo zeskanuj go w nizszej rozdzielczosci i wgraj ponownie. '
        + 'Ponawianie tego samego pliku nie pomoze.'
      );
      return;
    }

    setFileName(file.name);
    setStep('parsing');

    try {
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Store base64 for later Drive upload
      setPdfBase64(base64);

      // Call Cloud Function
      const result = await parseContractFunction({ pdfBase64: base64, fileName: file.name });

      if (result.data.success) {
        const data = result.data.data;
        setParsedData(data);

        // Fill editable fields
        setContractNumber(data.contract_number || '');
        setContractDate(data.contract_date || '');
        setFlatCode(data.flat_code || '');
        setFlatSize(data.flat_size || '');
        setPriceGross(data.price_gross || '');
        setParkingSpace(data.parking_space || '');
        setStorageUnit(data.storage_unit || '');
        setAccountNumber(data.buyer_account_number || '');
        setAccountNote(data.buyer_account_number_uwaga || '');

        if (data.buyers && data.buyers.length > 0) {
          setBuyers(data.buyers.map(b => ({
            name: b.name || '',
            surname: b.surname || '',
            address: b.address || '',
            pesel: b.pesel || '',
            nip: b.nip || '',
            buyerType: b.buyer_type === 'firma' ? 'firma' : 'osoba_fizyczna',
            companyName: b.company_name || '',
            krs: b.krs || '',
            share: b.share || '',
          })));
        }

        // Ownership structure
        if (data.ownership_type) setOwnershipType(data.ownership_type);
        setMaritalCommunity(
          String(data.marital_community || '').toLowerCase() === 'tak' ||
          data.ownership_type === 'joint_marital'
        );

        if (data.payment_schedule && data.payment_schedule.length > 0) {
          setPaymentSchedule(data.payment_schedule.map(p => ({
            stage: p.stage || '',
            amount: p.amount || '',
            due_date: p.due_date || '',
            description: p.description || '',
          })));
        }

        setStep('review');
      }
    } catch (error) {
      console.error('Error parsing contract:', error);
      // Surface the server's user-facing message for known cases (Gemini
      // overload / rate limit); fall back to a generic message otherwise.
      // `invalid-argument` dochodzi tu miedzy innymi przy zbyt duzym zadaniu.
      // Wczesniej wpadalo w komunikat domyslny, ktory kazal probowac ponownie -
      // a akurat w tym wypadku ponowienie nigdy nie zadziala.
      const code = error?.code || '';
      const PRZEPUSZCZANE = [
        'functions/unavailable',
        'functions/resource-exhausted',
        'functions/invalid-argument',
      ];
      const friendly = PRZEPUSZCZANE.includes(code) ? (error?.message || '') : '';
      showError(friendly || 'Blad parsowania dokumentu. Sprobuj ponownie.');
      setStep('upload');
    }
  };

  const handleBuyerChange = (index, field, value) => {
    const updated = [...buyers];
    updated[index][field] = value;
    setBuyers(updated);
  };

  const addBuyer = () => {
    setBuyers([...buyers, { name: '', surname: '', address: '', pesel: '', nip: '', buyerType: 'osoba_fizyczna', companyName: '', krs: '', share: '' }]);
  };

  // Parse a share string ("1/2", "50%", "0.5") into a fraction of 1
  const parseShare = (s) => {
    if (!s) return 0;
    const str = String(s).trim().toLowerCase();
    if (str === 'wspolnosc' || str === 'wspólnosc' || str === 'wspólność') return 0;
    if (str.includes('/')) {
      const [a, b] = str.split('/').map((x) => parseFloat(x));
      return b ? a / b : 0;
    }
    if (str.includes('%')) return (parseFloat(str) || 0) / 100;
    const n = parseFloat(str);
    if (isNaN(n)) return 0;
    return n > 1 ? n / 100 : n;
  };

  const shareSum = ownershipType === 'fractional'
    ? buyers.reduce((acc, b) => acc + parseShare(b.share), 0)
    : 1;
  const shareSumOk = Math.abs(shareSum - 1) < 0.001;
  const shareSumLabel = `${(shareSum * 100).toFixed(1)}%`;

  // Flat-level buyer category: company if ANY buyer is a business (firma/JDG), else individuals
  const buyerCategory = buyers.some((b) => b.buyerType === 'firma') ? 'company' : 'individual';

  const removeBuyer = (index) => {
    if (buyers.length === 1) return;
    setBuyers(buyers.filter((_, i) => i !== index));
  };

  // Payment schedule editing
  const handlePaymentChange = (index, field, value) => {
    const updated = [...paymentSchedule];
    updated[index][field] = value;
    setPaymentSchedule(updated);
  };

  const addPayment = () => {
    const nextStage = paymentSchedule.length
      ? String((parseInt(paymentSchedule[paymentSchedule.length - 1].stage, 10) || paymentSchedule.length) + 1)
      : '1';
    setPaymentSchedule([...paymentSchedule, { stage: nextStage, amount: '', due_date: '', description: '' }]);
  };

  const removePayment = (index) => {
    setPaymentSchedule(paymentSchedule.filter((_, i) => i !== index));
  };

  // Validation: PDF vs UI selection
  const expectedSeller = (investmentDoc && investmentDoc.company && investmentDoc.company.name) || '';
  const expectedInvestmentName = (investmentDoc && (investmentDoc.name || investmentDoc.location)) || '';
  const pdfSeller = (parsedData && parsedData.seller_name) || '';
  const pdfInvestmentName = (parsedData && parsedData.investment_name) || '';
  const sellerMismatch = pdfSeller && expectedSeller && !looseMatch(pdfSeller, expectedSeller);
  const investmentMismatch = pdfInvestmentName && expectedInvestmentName && !looseMatch(pdfInvestmentName, expectedInvestmentName);

  const handleSave = async () => {
    if (!investmentId || !flatCode) {
      showError('Brak ID inwestycji lub kodu lokalu');
      return;
    }

    setStep('saving');

    try {
      // 1. Find or create flat
      const { getDocs } = await import('firebase/firestore');
      const flatsRef = collection(firestore, `investments/${investmentId}/flats`);
      const flatsSnap = await getDocs(flatsRef);

      let flatId = null;
      let flatRef = null;

      // Find existing flat by code
      for (const flatDoc of flatsSnap.docs) {
        if (flatDoc.data().code === flatCode) {
          flatId = flatDoc.id;
          flatRef = doc(firestore, `investments/${investmentId}/flats/${flatId}`);
          break;
        }
      }

      if (!flatId) {
        showError(`Lokal ${flatCode} nie znaleziony w tej inwestycji`);
        setStep('review');
        return;
      }

      // Rachunek nabywcy: sprawdzany PRZED zapisem kartotek. Numer z bledem
      // przypialby wplaty do cudzego lokalu albo do zadnego, a inny rachunek
      // niz juz wpisany w lokal to sprzecznosc do wyjasnienia, nie do nadpisania.
      let rachunekDoZapisu = null;
      if (accountNumber.trim()) {
        const nrb = sprawdzNrb(accountNumber);
        if (!nrb.ok) {
          showError(`Rachunek nabywcy: ${nrb.powod}. Przepisz go z aktu albo zostaw puste.`);
          setStep('review');
          return;
        }
        const flatSnapNow = await getDoc(flatRef);
        const obecny = cyfryNrb((flatSnapNow.data() || {}).account_number);
        if (obecny && obecny !== nrb.cyfry) {
          showError(`Lokal ${flatCode} ma juz rachunek ${(flatSnapNow.data() || {}).account_number}, `
            + `a w umowie jest ${nrb.sformatowany}. Nic nie zapisano - wyjasnij, ktory jest wlasciwy.`);
          setStep('review');
          return;
        }
        if (!obecny) rachunekDoZapisu = nrb.sformatowany;
      }

      // 2. Create customer documents
      const clientIds = [];

      for (const buyer of buyers) {
        const isCompany = buyer.buyerType === 'firma';
        if (!buyer.name && !buyer.surname && !buyer.companyName) continue;

        const displayName = isCompany
          ? (buyer.companyName || '').trim()
          : `${buyer.name} ${buyer.surname}`.trim();

        // Share: explicit fraction for fractional ownership; joint marital = bezudziałowo; sole = całość
        const share = ownershipType === 'fractional'
          ? (buyer.share || '')
          : (ownershipType === 'joint_marital' ? 'wspolnosc' : '1/1');

        const customerData = {
          name: isCompany ? displayName : buyer.name,
          surname: isCompany ? '' : buyer.surname,
          buyers: [{
            name: displayName,
            address: buyer.address,
            pesel: isCompany ? '' : buyer.pesel,
            nip: buyer.nip,
            type: isCompany ? 'company' : 'individual',
            companyName: isCompany ? (buyer.companyName || '') : '',
            krs: isCompany ? (buyer.krs || '') : '',
            share,
          }],
          purchasedFlats: [{
            investmentId,
            flatId,
            purchaseDate: contractDate || new Date().toISOString().split('T')[0],
          }],
        };

        const customerRef = await addDoc(collection(firestore, 'customers'), customerData);
        clientIds.push(customerRef.id);
      }

      // 3. Update flat with client IDs and contract info
      const priceGrossNum = parseFloat(priceGross) || 0;
      await updateDoc(flatRef, {
        status: 'sold',
        clients: clientIds,
        contract_number: contractNumber,
        contract_date: contractDate,
        an_amount: priceGrossNum,
        size: parseFloat(flatSize) || 0,
        parking_space: parkingSpace || null,
        storage_unit: storageUnit || null,
        ...(rachunekDoZapisu ? { account_number: rachunekDoZapisu } : {}),
        ownership: {
          type: ownershipType, // sole | joint_marital | fractional
          maritalCommunity: !!maritalCommunity,
          buyerCategory, // individual | company (JDG/spolka)
        },
      });

      // 4. Create payment schedule if available
      const primaryClientId = clientIds[0] || null;
      for (const payment of paymentSchedule) {
        if (!payment.amount) continue;

        const amountNum = parseFloat(payment.amount) || 0;
        const percentage = priceGrossNum > 0
          ? Math.round((amountNum / priceGrossNum) * 10000) / 100  // 2 decimal places
          : 0;

        await addDoc(collection(firestore, `investments/${investmentId}/flats/${flatId}/payments`), {
          stageNumber: parseInt(payment.stage) || 0,
          paymentNumber: parseInt(payment.stage) || 0,
          amount: amountNum,
          dueDate: payment.due_date || '',
          description: payment.description || '',
          percentage,
          payment_status: 'Oczekuje',
          paidAmount: 0,
          clientId: primaryClientId,
          clientIds,
        });
      }

      // 5. Upload PDF to Google Drive (if folder configured)
      if (pdfBase64 && investmentDoc?.contractsDriveUrl) {
        try {
          const folderMatch = investmentDoc.contractsDriveUrl.match(/folders\/([a-zA-Z0-9_-]+)/);
          if (folderMatch) {
            const driveFolderId = folderMatch[1];
            const buyerName = buyers[0] ? `${buyers[0].name} ${buyers[0].surname}`.trim() : '';
            const contractLabel = (contractNumber || 'umowa').replace(/\//g, '_');
            const driveFileName = `Sovo Pay ${flatCode}. Umowa deweloperska ${contractLabel}${buyerName ? ' - ' + buyerName : ''}.pdf`;
            await uploadToDriveFunction({ pdfBase64, fileName: driveFileName, driveFolderId });
          }
        } catch (driveError) {
          console.error('Drive upload failed (data saved OK):', driveError);
          showError('Dane zapisane, ale upload PDF na Drive nie powiódł się');
        }
      }

      showSuccess(`Klient dodany do lokalu ${flatCode}`);
      resetForm();
      if (onSave) onSave();
      onClose();
    } catch (error) {
      console.error('Error saving contract data:', error);
      showError('Blad zapisu danych');
      setStep('review');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={() => { resetForm(); onClose(); }} wide>
      <div className="p-6">
        <BlockTitle>Dodaj umowe / akt notarialny</BlockTitle>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="mt-6 flex flex-col items-center gap-4 py-12">
            <div className="text-gray-500 text-sm">Wybierz plik PDF aktu notarialnego lub umowy deweloperskiej</div>
            <label className="cursor-pointer bg-[var(--maroon)] text-white px-6 py-3 rounded-lg hover:opacity-90 transition">
              Wybierz plik PDF
              <input type="file" accept=".pdf" className="hidden" onChange={handleFileUpload} />
            </label>
          </div>
        )}

        {/* Step 2: Parsing */}
        {step === 'parsing' && (
          <div className="mt-6 flex flex-col items-center gap-4 py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[var(--maroon)] border-t-transparent rounded-full"></div>
            <div className="text-gray-600">Analizowanie dokumentu: {fileName}</div>
            <div className="text-gray-400 text-sm">To moze potrwac ok. 30 sekund...</div>
          </div>
        )}

        {/* Step 3: Review */}
        {step === 'review' && (
          <div className="mt-4">
            <div className="text-sm text-green-600 mb-4">Dokument przeanalizowany. Sprawdz i popraw dane:</div>

            {/* Validation warnings */}
            {(sellerMismatch || investmentMismatch) && (
              <div className="mb-4 border border-amber-300 bg-amber-50 rounded-md p-3 text-sm space-y-1">
                <div className="font-semibold text-amber-800">Uwaga: dane z PDF moga nie pasowac do wybranej inwestycji</div>
                {sellerMismatch && (
                  <div className="text-amber-800">
                    Sprzedawca w PDF: <span className="font-mono">{pdfSeller}</span>
                    {' '}vs spolka inwestycji: <span className="font-mono">{expectedSeller}</span>
                  </div>
                )}
                {investmentMismatch && (
                  <div className="text-amber-800">
                    Nazwa inwestycji w PDF: <span className="font-mono">{pdfInvestmentName}</span>
                    {' '}vs wybrana: <span className="font-mono">{expectedInvestmentName}</span>
                  </div>
                )}
                <div className="text-amber-700 text-xs">Sprawdz, czy uploadujesz wlasciwy dokument.</div>
              </div>
            )}

            {/* Contract info */}
            <div className="grid grid-cols-4 gap-4">
              <div>
                <FormLabel>Numer umowy</FormLabel>
                <FormInput value={contractNumber} onChange={(e) => setContractNumber(e.target.value)} />
              </div>
              <div>
                <FormLabel>Data umowy</FormLabel>
                <FormInput type="date" value={contractDate} onChange={(e) => setContractDate(e.target.value)} />
              </div>
              <div>
                <FormLabel>Kod lokalu</FormLabel>
                <FormInput value={flatCode} onChange={(e) => setFlatCode(e.target.value)} />
              </div>
              <div>
                <FormLabel>Powierzchnia (m2)</FormLabel>
                <FormInput type="number" step="0.01" value={flatSize} onChange={(e) => setFlatSize(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mt-3">
              <div>
                <FormLabel>Cena brutto (PLN)</FormLabel>
                <FormInput type="number" value={priceGross} onChange={(e) => setPriceGross(e.target.value)} />
              </div>
              <div>
                <FormLabel>Miejsce parkingowe</FormLabel>
                <FormInput value={parkingSpace} onChange={(e) => setParkingSpace(e.target.value)} placeholder="np. P12" />
              </div>
              <div>
                <FormLabel>Komorka lokatorska</FormLabel>
                <FormInput value={storageUnit} onChange={(e) => setStorageUnit(e.target.value)} placeholder="np. K5" />
              </div>
            </div>

            <div className="mt-3">
              <FormLabel>Rachunek nabywcy (subkonto z aktu)</FormLabel>
              <FormInput
                value={accountNumber}
                onChange={(e) => { setAccountNumber(e.target.value); setAccountNote(''); }}
                placeholder="np. 23 1140 1124 6631 0200 0000 0005"
              />
              {(() => {
                if (accountNote) return <p className="text-xs text-red-600 mt-1">{accountNote}</p>;
                if (!accountNumber.trim()) {
                  return <p className="text-xs text-amber-700 mt-1">Bez rachunku wplaty z banku nie trafia do tego lokalu.</p>;
                }
                const w = sprawdzNrb(accountNumber);
                return w.ok
                  ? <p className="text-xs text-green-700 mt-1">Numer poprawny.</p>
                  : <p className="text-xs text-red-600 mt-1">{w.powod}</p>;
              })()}
            </div>

            {/* Ownership structure */}
            <div className="mt-4 border border-gray-200 rounded-md p-3 bg-gray-50">
              <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Forma nabycia</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FormLabel>Typ wlasnosci</FormLabel>
                  <select
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                    value={ownershipType}
                    onChange={(e) => setOwnershipType(e.target.value)}
                  >
                    <option value="sole">Jeden nabywca</option>
                    <option value="joint_marital">Malzenstwo - wspolnosc ustawowa (bezudzialowo)</option>
                    <option value="fractional">Udzialy ulamkowe</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm text-gray-700 pb-2">
                    <input
                      type="checkbox"
                      checked={maritalCommunity}
                      onChange={(e) => setMaritalCommunity(e.target.checked)}
                    />
                    Wspolnota majatkowa malzenska
                  </label>
                </div>
              </div>
              {ownershipType === 'fractional' && (
                <div className={`mt-2 text-xs ${shareSumOk ? 'text-gray-500' : 'text-red-600'}`}>
                  Suma udzialow: {shareSumLabel}{shareSumOk ? '' : ' (powinna wynosic 100% = 1/1)'}
                </div>
              )}
              <div className="mt-2 text-xs text-gray-600">
                Kategoria nabywcy (poziom mieszkania):{' '}
                <span className="font-semibold">{buyerCategory === 'company' ? 'Firma / JDG' : 'Osoby fizyczne'}</span>
              </div>
            </div>

            {/* Buyers */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">Nabywcy</div>
                <Button variant="bordered" className="text-xs py-1 px-3" onClick={addBuyer}>+ Dodaj nabywce</Button>
              </div>
              {buyers.map((buyer, index) => (
                <div key={index} className="border border-gray-200 rounded-md p-3 mb-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs text-gray-400">#{index + 1}</span>
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name={`btype-${index}`}
                          checked={buyer.buyerType !== 'firma'}
                          onChange={() => handleBuyerChange(index, 'buyerType', 'osoba_fizyczna')}
                        />
                        Osoba fizyczna
                      </label>
                      <label className="flex items-center gap-1 text-sm">
                        <input
                          type="radio"
                          name={`btype-${index}`}
                          checked={buyer.buyerType === 'firma'}
                          onChange={() => handleBuyerChange(index, 'buyerType', 'firma')}
                        />
                        Firma / JDG
                      </label>
                      {ownershipType === 'fractional' && (
                        <span className="flex items-center gap-1">
                          <span className="text-xs text-gray-500">Udzial:</span>
                          <input
                            className="w-20 border border-gray-300 rounded px-2 py-1 text-sm"
                            placeholder="1/2"
                            value={buyer.share}
                            onChange={(e) => handleBuyerChange(index, 'share', e.target.value)}
                          />
                        </span>
                      )}
                    </div>
                    {buyers.length > 1 && (
                      <button onClick={() => removeBuyer(index)} className="text-red-400 hover:text-red-600 text-lg">x</button>
                    )}
                  </div>

                  {buyer.buyerType === 'firma' ? (
                    <div className="grid grid-cols-[2fr_1fr_1fr] gap-2">
                      <div>
                        <FormLabel>Nazwa firmy</FormLabel>
                        <FormInput value={buyer.companyName} onChange={(e) => handleBuyerChange(index, 'companyName', e.target.value)} />
                      </div>
                      <div>
                        <FormLabel>KRS</FormLabel>
                        <FormInput value={buyer.krs} onChange={(e) => handleBuyerChange(index, 'krs', e.target.value)} />
                      </div>
                      <div>
                        <FormLabel>NIP</FormLabel>
                        <FormInput value={buyer.nip} onChange={(e) => handleBuyerChange(index, 'nip', e.target.value)} />
                      </div>
                      <div className="col-span-3">
                        <FormLabel>Adres siedziby</FormLabel>
                        <FormInput value={buyer.address} onChange={(e) => handleBuyerChange(index, 'address', e.target.value)} />
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-[1fr_1fr_1fr_120px_120px] gap-2">
                      <div>
                        <FormLabel>Imie</FormLabel>
                        <FormInput value={buyer.name} onChange={(e) => handleBuyerChange(index, 'name', e.target.value)} />
                      </div>
                      <div>
                        <FormLabel>Nazwisko</FormLabel>
                        <FormInput value={buyer.surname} onChange={(e) => handleBuyerChange(index, 'surname', e.target.value)} />
                      </div>
                      <div>
                        <FormLabel>Adres</FormLabel>
                        <FormInput value={buyer.address} onChange={(e) => handleBuyerChange(index, 'address', e.target.value)} />
                      </div>
                      <div>
                        <FormLabel>PESEL</FormLabel>
                        <FormInput value={buyer.pesel} onChange={(e) => handleBuyerChange(index, 'pesel', e.target.value)} />
                      </div>
                      <div>
                        <FormLabel>NIP</FormLabel>
                        <FormInput value={buyer.nip} onChange={(e) => handleBuyerChange(index, 'nip', e.target.value)} />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Payment schedule (editable) */}
            <div className="mt-4">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs font-semibold text-gray-500 uppercase">Harmonogram platnosci</div>
                <Button variant="bordered" className="text-xs py-1 px-3" onClick={addPayment}>+ Dodaj rate</Button>
              </div>
              {paymentSchedule.length === 0 && (
                <div className="text-xs text-gray-400 italic">Brak rat. Mozesz dodac rece.</div>
              )}
              {paymentSchedule.map((p, index) => {
                const amountNum = parseFloat(p.amount) || 0;
                const priceNum = parseFloat(priceGross) || 0;
                const pct = priceNum > 0 ? (amountNum / priceNum) * 100 : 0;
                return (
                  <div key={index} className="grid grid-cols-[80px_140px_150px_1fr_70px_30px] gap-2 items-end mb-2">
                    <div>
                      {index === 0 && <FormLabel>Etap</FormLabel>}
                      <FormInput value={p.stage} onChange={(e) => handlePaymentChange(index, 'stage', e.target.value)} />
                    </div>
                    <div>
                      {index === 0 && <FormLabel>Kwota (PLN)</FormLabel>}
                      <FormInput type="number" step="0.01" value={p.amount} onChange={(e) => handlePaymentChange(index, 'amount', e.target.value)} />
                    </div>
                    <div>
                      {index === 0 && <FormLabel>Termin</FormLabel>}
                      <FormInput type="date" value={p.due_date} onChange={(e) => handlePaymentChange(index, 'due_date', e.target.value)} />
                    </div>
                    <div>
                      {index === 0 && <FormLabel>Opis</FormLabel>}
                      <FormInput value={p.description} onChange={(e) => handlePaymentChange(index, 'description', e.target.value)} />
                    </div>
                    <div className="text-xs text-gray-500 pb-2 text-right">
                      {pct ? `${pct.toFixed(2)}%` : '—'}
                    </div>
                    <div>
                      <button onClick={() => removePayment(index)} className="text-red-400 hover:text-red-600 text-lg pb-2">x</button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Actions */}
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="bordered" onClick={() => { resetForm(); onClose(); }}>Anuluj</Button>
              <Button variant="default" onClick={handleSave}>Zapisz klienta</Button>
            </div>
          </div>
        )}

        {/* Step 4: Saving */}
        {step === 'saving' && (
          <div className="mt-6 flex flex-col items-center gap-4 py-12">
            <div className="animate-spin w-8 h-8 border-4 border-[var(--maroon)] border-t-transparent rounded-full"></div>
            <div className="text-gray-600">Zapisywanie danych...</div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default UploadContractModal;
